import url from "./url";
import { getAuthToken, logoutUser } from "../service/authService";
import { toast } from "react-toastify";

/**
 * HTTP Request Handler for Representative Module
 * Features:
 * - JWT token management
 * - Automatic logout on authentication failure
 * - Error handling and user feedback
 */

// Handle authentication failure - logout user and redirect
const handleAuthFailure = async (error) => {
  console.error('Authentication failure:', error);

  try {
    await logoutUser();

    // Clear all auth data
    localStorage.removeItem('authToken');
    localStorage.removeItem('representativeUser');
    localStorage.removeItem('selectedProgramme');
    localStorage.removeItem('programmes');

    // Show user-friendly message
    toast.error('Your session has expired. Please sign in again.', {
      position: 'top-center',
      autoClose: 5000,
    });

    // Redirect to login after a brief delay
    setTimeout(() => {
     // window.location.href = '/login';
    }, 1000);

  } catch (logoutError) {
    console.error('Error during logout:', logoutError);
    // Force redirect even if logout fails
   // window.location.href = '/login';
  }
};

// Check if error indicates authentication failure
const isAuthError = (response) => {
  if (!response) return false;

  const errorIndicators = [
    'session has expired',
    'token expired',
    'invalid token',
    'unauthorized',
    'authentication failed',
    'access denied',
    'jwt expired',
    'token invalid'
  ];

  const message = (response.message || '').toLowerCase();
  return response.type === 'error' &&
    (response.status === 401 ||
      response.status === 403 ||
      errorIndicators.some(indicator => message.includes(indicator)));
};

const HttpRequest = async (link, head = {}) => {
  try {
    // Define public endpoints that don't require authentication
    const publicEndpoints = [
      '/auth/login',
      '/nuc_accreditation/login',
      '/nuc_accreditation/panel/auth',
    ];

    // Check if this is a public endpoint
    const isPublicEndpoint = publicEndpoints.some(endpoint => link.includes(endpoint));

    // Get JWT token from localStorage
    const authToken = getAuthToken();

    // Skip auth check for public endpoints
    if (!isPublicEndpoint && !authToken) {
      console.error('No authentication token found');
      await handleAuthFailure({ type: 'error', message: 'No authentication token found' });
      return { type: "error", message: "Authentication failed. Please sign in again." };
    }

    // Properly merge headers, ensuring auth headers are not overridden
    const headers = {
      ...head,
      credentials: "include",
      headers: {
        "Content-type": "application/json",
        ...head.headers,
        ...(authToken && !isPublicEndpoint && {
          "Authorization": `Bearer ${authToken}`,
        })
      },
    };

    const req = await fetch(`${url}${link}`, headers);

    // Handle HTTP errors
    if (!req.ok) {
      console.error('HTTP Error:', {
        status: req.status,
        statusText: req.statusText,
        endpoint: `${url}${link}`
      });

      if (req.status === 401 || req.status === 403) {
        const errorData = await req.json().catch(() => ({
          type: 'error',
          message: `HTTP ${req.status}: Authentication failed`
        }));
        await handleAuthFailure(errorData);
        return { type: "error", message: "Authentication failed. Please sign in again." };
      }
    }

    const res = await req.json();

    // Check for authentication errors in response
    if (isAuthError(res)) {
      await handleAuthFailure(res);
      return { type: "error", message: "Authentication failed. Please sign in again." };
    }

    return res;
  } catch (error) {
    console.error('HttpRequest error:', error);

    // Handle network errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return {
        type: "error",
        message: "Network error. Please check your internet connection."
      };
    }

    return {
      type: "error",
      message: error.message || "An unexpected error occurred"
    };
  }
};

export default HttpRequest;